import { t, Trans } from '@lingui/macro'
import Tooltip from '@mui/material/Tooltip'
import { useGreenlightPatientWorkflow } from '@om1/aspen-api'
import { useEffect, useMemo, useState } from 'react'

const GreenlightPatientWorkflowButton = ({
    patientId,
    hasProviders,
    enrolled,
    onSuccess
}: {
    patientId: string
    hasProviders?: boolean
    enrolled?: boolean
    onSuccess?: () => void
}) => {
    const { taskStatus, loading, start } = useGreenlightPatientWorkflow(patientId)
    const [isButtonLoading, setButtonLoading] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    const handleButtonClick = async () => {
        setButtonLoading(true)
        try {
            await start(patientId)
        } catch (err) {
            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
            console.error('Error triggering workflow:', err)
        } finally {
            setButtonLoading(false)
        }
    }

    useEffect(() => {
        if (taskStatus?.status === 'SUCCESS') {
            onSuccess?.()
        }
    }, [onSuccess, taskStatus?.status])

    const renderTaskStatus = () => {
        if (!taskStatus) return null

        switch (taskStatus.status) {
            case 'PENDING':
                return (
                    <p>
                        <Trans>Task is pending...</Trans>
                    </p>
                )
            case 'STARTED':
                return (
                    <p>
                        <Trans>Task has started...</Trans>
                    </p>
                )
            case 'RETRY':
                return (
                    <p>
                        <Trans>Task is retrying...</Trans>
                    </p>
                )
            case 'REVOKED':
                return (
                    <p>
                        <Trans>Task has been revoked.</Trans>
                    </p>
                )
            case 'FAILURE':
                return (
                    <p>
                        <Trans>Task failed. Please try again.</Trans>
                    </p>
                )
            case 'SUCCESS':
                return (
                    <p>
                        <Trans>Task completed successfully!</Trans>
                    </p>
                )
            default:
                return (
                    <p>
                        <Trans>Unknown task status.</Trans>
                    </p>
                )
        }
    }

    const renderButtonText = () => {
        if (isButtonLoading || loading) {
            return t`Loading...`
        } else if (taskStatus?.status !== undefined && taskStatus?.status !== null) {
            if (taskStatus.status === 'FAILURE' && isHovered) {
                return t`Retry`
            } else {
                return renderTaskStatus()
            }
        } else {
            return t`Associate Patient in Greenlight`
        }
    }

    const isButtonDisabled = useMemo(
        () =>
            isButtonLoading ||
            loading ||
            !hasProviders ||
            enrolled ||
            (taskStatus?.status !== undefined && taskStatus?.status !== null && taskStatus?.status !== 'FAILURE'),
        [isButtonLoading, loading, hasProviders, enrolled, taskStatus]
    )

    return (
        <div>
            <Tooltip
                title={
                    !hasProviders ? t`User is not associated with any providers` : enrolled ? t`Patient is already associated with Greenlight` : ''
                }
                arrow
            >
                <span>
                    <button
                        onClick={handleButtonClick}
                        disabled={isButtonDisabled}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        className={`
                            flex items-center justify-center gap-1.5
                            px-3 py-1.5
                            rounded-md
                            transition-colors
                            text-sm font-medium
                            min-w-[140px]
                            shadow-sm
                            h-[36px]
                            ${
                                loading || !hasProviders || enrolled || (taskStatus?.status !== undefined && taskStatus?.status !== null)
                                    ? taskStatus?.status === 'SUCCESS'
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : taskStatus?.status === 'FAILURE'
                                        ? 'bg-red-400 text-white hover:bg-red-500'
                                        : taskStatus?.status === 'STARTED' || taskStatus?.status === 'PENDING' || loading
                                        ? 'bg-orange-400 text-white hover:bg-orange-500'
                                        : 'bg-gray-400 text-white'
                                    : 'bg-primary-main text-white hover:bg-primary-dark'
                            }
                            ${!hasProviders || isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        style={{ pointerEvents: !hasProviders ? 'none' : 'auto' }}
                    >
                        <span>{renderButtonText()}</span>
                    </button>
                </span>
            </Tooltip>
        </div>
    )
}

export default GreenlightPatientWorkflowButton
